import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { CourseEmployeesReportFilterI, EmployeesByCourseReportFilterI } from 'types';
import useTranslation from 'Hooks/useTranslation';
import useDebounce from 'Hooks/useDebounce';
import { useEffect } from 'react';
import useTerminalsApi from 'Hooks/useTerminalsApi';
import useDepartmentsApi from 'Hooks/useDepartmentsApi';
import useJobTitlesApi from 'Hooks/useJobTitlesApi';
import useJobTitlesTypeApi from 'Hooks/useJobTitlesTypeApi';
import { defaultValue, courseEmployeesReportFilterSchema } from '../schemas/courses-employees-report-filter';

interface Props {
  onSearch: (filters: EmployeesByCourseReportFilterI) => void;
}

const useCourseEmployeesReportFilters = ({ onSearch }: Props) => {
  const { t2 } = useTranslation('course_employees_report');

  const {
    control,
    reset,
    setValue,
    getValues,
    formState: { isDirty },
    watch,
  } = useForm<CourseEmployeesReportFilterI>({
    resolver: zodResolver(courseEmployeesReportFilterSchema),
    defaultValues: defaultValue,
  });

  const { data: terminalsData, loading: terminalsLoading, error: terminalsError } = useTerminalsApi({ init: true });

  const {
    data: departmentsData,
    error: departmentsError,
    loading: departmentsLoading,
  } = useDepartmentsApi({ init: true });

  const { data: jobTitlesData, loading: jobTitlesLoading, error: jobTitlesError } = useJobTitlesApi({ init: true });

  const {
    data: jobTitlesTypesData,
    loading: jobTitlesTypesLoading,
    error: jobTitlesTypesError,
  } = useJobTitlesTypeApi({ init: true });

  const onReset = () => {
    reset(defaultValue);
    onSearch({});
  };

  // Watch for changes in the name field
  const nameValue = watch('fullName');
  const debouncedName = useDebounce(nameValue, 500);

  // Effect to handle debounced search
  useEffect(() => {
    if (debouncedName !== undefined) {
      const values = getValues();
      onSearch(values);
    }
  }, [debouncedName]);

  // Handle other filter changes immediately
  const handleFilterChange = () => {
    const values = getValues();
    onSearch(values);
  };

  return {
    control,
    reset,
    setValue,
    getValues,
    isDirty,
    onReset,
    t2,
    // Data
    terminalsData,
    departmentsData,
    jobTitlesData,
    jobTitlesTypesData,
    // Loading
    terminalsLoading,
    departmentsLoading,
    jobTitlesLoading,
    jobTitlesTypesLoading,
    // Error
    terminalsError,
    departmentsError,
    jobTitlesError,
    jobTitlesTypesError,
    handleFilterChange,
  };
};

export default useCourseEmployeesReportFilters;
